import { QueryClient, useQuery, UseQueryOptions } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { SuggestionRequest } from 'types'

export const designEditRequestsQueryKey = (designKey: string) => ['designs', designKey, 'edit_design_requests'] as const

const fetchDesignEditRequests = async (workspaceId: number, designKey: string): Promise<SuggestionRequest[]> => {
  const res = await sendToApi<{ requests: SuggestionRequest[] }>(
    `/w/${workspaceId}/designs/${designKey}/edit_design_requests`,
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    throw new Error(typeof res.data === 'string' ? res.data : 'Failed to load edit previews')
  }
  return res.data.requests
}

/** Warms the same cache entry as {@link useDesignEditRequests} (e.g. while the post preview is open). */
export function prefetchDesignEditRequests(
  queryClient: QueryClient,
  workspaceId: number | null | undefined,
  designKey: string | null | undefined,
): Promise<void> | void {
  if (!workspaceId || !designKey) return
  return queryClient.prefetchQuery(designEditRequestsQueryKey(designKey), () =>
    fetchDesignEditRequests(workspaceId, designKey),
  )
}

export function useDesignEditRequests(
  designKey: string,
  enabled: boolean,
  options?: Pick<UseQueryOptions<SuggestionRequest[]>, 'refetchOnWindowFocus'>,
) {
  const workspaceId = useCurrentWorkspaceId()
  return useQuery(designEditRequestsQueryKey(designKey), () => fetchDesignEditRequests(workspaceId, designKey), {
    enabled: Boolean(enabled && workspaceId && designKey),
    ...options,
  } as Omit<UseQueryOptions<SuggestionRequest[]>, 'queryKey' | 'queryFn'>)
}
